import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check } from 'lucide-react';

interface Option {
    value: string | number;
    label: string;
}

interface SelectProps {
    options: Option[];
    value: string | number;
    onChange: (value: string | number) => void;
    label?: string;
    placeholder?: string;
    required?: boolean;
    disabled?: boolean;
    className?: string; // Allow minimal overrides
}

export const Select: React.FC<SelectProps> = ({
    options,
    value,
    onChange,
    label,
    placeholder = "Seleccionar...",
    required,
    disabled = false
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({}); // Store calculated position

    const selectedOption = options.find(opt => opt.value === value);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        // Scroll listener to close dropdown on scroll (simpler than repositioning)


        document.addEventListener('mousedown', handleClickOutside);
        // Removed scroll listener to allow scrolling without closing
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    const handleToggle = () => {
        if (disabled) return;
        if (!isOpen && containerRef.current) {
            const rect = containerRef.current.getBoundingClientRect();
            setDropdownStyle({
                position: 'fixed',
                top: rect.bottom + 8,
                left: rect.left,
                width: rect.width,
                zIndex: 9999
            });
        }
        setIsOpen(!isOpen);
    };

    const handleSelect = (val: string | number) => {
        if (disabled) return;
        onChange(val);
        setIsOpen(false);
    };

    // Use Portal-like behavior by rendering fixed at root level if needed, 
    // but React pure fixed works if z-index is high enough and no transform parent.
    // If transforms exist, fixed might be tricky, but usually works better than absolute in tables.

    return (
        <div className="relative" ref={containerRef}>
            {label && (
                <label className="text-xs font-bold text-gray-700 mb-2 block uppercase tracking-wide">
                    {label} {required && <span className="text-red-500">*</span>}
                </label>
            )}

            <div
                onClick={handleToggle}
                className={`
                    w-full pl-4 pr-10 py-3 bg-gray-50 border border-gray-200 text-gray-700 text-sm rounded-xl 
                    focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none transition-all 
                    cursor-pointer hover:bg-white hover:shadow-md font-medium flex items-center justify-between relative
                    ${isOpen ? 'ring-2 ring-orange-500 bg-white' : ''}
                    ${disabled ? 'opacity-50 cursor-not-allowed hover:bg-gray-50 hover:shadow-none' : ''}
                `}
            >
                <span className={`block truncate ${selectedOption ? "text-gray-900" : "text-gray-500 italic"}`}>
                    {selectedOption ? selectedOption.label : placeholder}
                </span>

                <div className={`absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}>
                    <ChevronDown className="w-5 h-5 text-gray-400 group-hover:text-orange-500" />
                </div>
            </div>

            {isOpen && !disabled && (
                <div
                    style={dropdownStyle}
                    className="fixed bg-white rounded-xl shadow-2xl border border-gray-100 max-h-60 overflow-y-auto animate-in fade-in zoom-in-95 duration-100"
                >
                    <ul className="py-1">
                        {options.length > 0 ? (
                            options.map((opt) => (
                                <li
                                    key={opt.value}
                                    onClick={() => handleSelect(opt.value)}
                                    className={`
                                        px-4 py-2.5 text-sm cursor-pointer transition-colors flex items-center justify-between
                                        ${opt.value === value ? 'bg-orange-50 text-orange-700 font-semibold' : 'text-gray-700 hover:bg-gray-50'}
                                    `}
                                >
                                    <span className="truncate mr-2">{opt.label}</span>
                                    {opt.value === value && <Check className="w-4 h-4 text-orange-500 flex-shrink-0" />}
                                </li>
                            ))
                        ) : (
                            <li className="px-4 py-3 text-sm text-gray-400 italic text-center">
                                No hay opciones disponibles
                            </li>
                        )}
                    </ul>
                </div>
            )}
        </div>
    );
};
